import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { AbstractAlcmeonBackendInterceptor } from './abstract-alcmeon-backend-interceptor';
import { retryWhen, mergeMap } from 'rxjs/operators';
import { MaintenanceModeService } from '../../shared/services/maintenance-mode.service';

const MAINTENANCE_HEADER_VALUE = 'maintenance';

/**
 * Interceptor that detects errors due to the current company being in maintenance mode.
 * In case of such an error, it calls the MaintenanceModeService to show the corresponding modal (if not done yet),
 * and retries the HTTP call when the MaintenanceModeService notifies that the maintenance is finished
 */
@Injectable()
export class MaintenanceModeInterceptor extends AbstractAlcmeonBackendInterceptor {
  constructor(private maintenanceModeService: MaintenanceModeService) {
    super();
  }

  /**
   * more info on the retryWhen operator:
   * https://rxjs.dev/api/operators/retryWhen
   * https://www.learnrxjs.io/learn-rxjs/operators/error_handling/retrywhen
   * https://medium.com/angular-in-depth/retry-failed-http-requests-in-angular-f5959d486294
   */
  protected doIntercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(retryWhen(errors => this.shouldRetryNotifier(errors)));
  }

  /**
   * Returns the notifier Observable that will control the retryWhen operator above.
   * If the error caught by retryWhen indicates that the backend is in maintenance mode,
   * the MaintenanceModeService is called to maybe initiate the maintenance mode (that is, display the corresponding modal)
   * and the returned Observable will emit when the maintenance is finished.
   * Otherwise (if the error caught by retryWhen is another error),
   * the returned Observable will immediately throw the error, so that retryWhen doesn't retry the call
   */
  private shouldRetryNotifier(errors: Observable<HttpErrorResponse>): Observable<any> {
    return errors.pipe(
      mergeMap(errorResponse =>
        this.shouldWaitForMaintenanceToFinish(errorResponse)
          ? this.maintenanceModeService.maybeInitMaintenanceModeThenReturnFinishedObservable()
          : throwError(errorResponse)
      )
    );
  }

  /**
   * Note: there is a special case for the endpoint used by the MaintenanceModeService
   * to check every 10s if the maintenance is still on-going:
   * we don't hold such requests here as the MaintenanceModeService needs to receive the responses
   */
  private shouldWaitForMaintenanceToFinish(errorResponse: HttpErrorResponse) {
    return this.hasMaintenanceModeHeader(errorResponse) && !this.isMaintenanceModeCheckURL(errorResponse);
  }

  private hasMaintenanceModeHeader(response: HttpErrorResponse): boolean {
    return response.headers.get('X-Alcmeon-State') === MAINTENANCE_HEADER_VALUE;
  }

  private isMaintenanceModeCheckURL(response: HttpErrorResponse): boolean {
    return response.url.endsWith(this.maintenanceModeService.maintenanceCheckUrl());
  }
}
